import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, Search, Filter, Download, Eye, Calendar, Briefcase, 
  GraduationCap, Clock, FileText, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getAssetUrl } from '@/utils/api';
import { expFromDate, expToDate } from '@/utils/seekerHelpers';

const APPLICATION_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-700' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-amber-100 text-amber-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-slate-100 text-slate-700' },
  { value: 'selected', label: 'Selected', color: 'bg-green-100 text-green-700' },
  { value: 'offer_made', label: 'Offer Made', color: 'bg-teal-100 text-teal-700' },
  { value: 'joined', label: 'Joined', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' }
];

const ApplicationManagement = () => {
  const user = getUser();
  const isHR = user?.role === 'hr';
  const employerId = isHR ? user.employer_id : user.id;
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobFilters, setJobFilters] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    job_id: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  
  // Status update modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  
  // Profile view modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, [currentPage]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
      if (filters.job_id) params.append('job_id', filters.job_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      // If HR, pass hr_id to filter applications for HR's jobs only
      if (isHR) {
        params.append('hr_id', user.id);
      }
      
      const response = await api.get(`/employer/applications/${employerId}?${params}`);
      setApplications(response.data.applications || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setJobFilters(response.data.job_filters || []);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchApplications();
  };

  const handleClearFilters = () => {
    setFilters({ job_id: '', status: '', from_date: '', to_date: '' });
    setCurrentPage(1);
    setTimeout(fetchApplications, 100);
  };

  const openStatusModal = (app) => {
    setSelectedApp(app);
    setNewStatus(app.status || 'new');
    setStatusNotes(app.status_notes || '');
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedApp) return;
    
    try {
      await api.put(`/employer/applications/${selectedApp.id}/status`, {
        status: newStatus,
        notes: statusNotes
      });
      toast.success('Application status updated');
      setShowStatusModal(false);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const viewProfile = (app) => {
    setViewingProfile(app.job_seeker);
    setShowProfileModal(true);
  };

  const downloadCV = (cvUrl) => {
    if (cvUrl) {
      window.open(getAssetUrl(cvUrl), '_blank');
    } else {
      toast.error('CV not available');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = APPLICATION_STATUSES.find(s => s.value === status) || APPLICATION_STATUSES[0];
    return (
      <Badge variant="outline" className={`${statusConfig.color} border-0`}>
        {statusConfig.label}
      </Badge>
    );
  };

  // Count applications with 'new' status
  const newApplicationsCount = applications.filter(app => app.status === 'new' || !app.status).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Application Management
          </h1>
          <p className="text-slate-600 mt-1">Manage and track all job applications</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          <Users className="w-5 h-5 mr-2" />
          {total} Applications
        </Badge>
      </div>

      {/* Reminder Notice for New Applications */}
      {newApplicationsCount > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Action Required: Update Application Status</p>
              <p className="text-sm text-amber-700 mt-1">
                You have <strong>{newApplicationsCount}</strong> application(s) with "New" status. 
                Please review and update the status of each application to help candidates know their progress.
                Click on the status badge in the table to change status.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-600" />
          <span className="font-medium text-slate-900">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="text-sm text-slate-600 mb-1 block">From Date</label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters({...filters, from_date: e.target.value})}
              data-testid="filter-from-date"
            />
          </div>
          <div>
            <label className="text-sm text-slate-600 mb-1 block">To Date</label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters({...filters, to_date: e.target.value})}
              data-testid="filter-to-date"
            />
          </div>
          <div>
            <label className="text-sm text-slate-600 mb-1 block">Job Title/Role</label>
            <Select value={filters.job_id} onValueChange={(v) => setFilters({...filters, job_id: v === 'all' ? '' : v})}>
              <SelectTrigger data-testid="filter-job">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobFilters.map(job => (
                  <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-slate-600 mb-1 block">Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {APPLICATION_STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters} data-testid="clear-btn">
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Applications Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12">#</TableHead>
                <TableHead>Candidate Name</TableHead>
                <TableHead>Job ID</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Applied Date</TableHead>
                <TableHead>Qualification</TableHead>
                <TableHead>Experience</TableHead>
                <TableHead>CV</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="text-slate-600 mt-2">Loading applications...</p>
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No applications found</p>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app, index) => (
                  <TableRow key={app.id} className="hover:bg-slate-50">
                    <TableCell>{(currentPage - 1) * 20 + index + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{app.candidate_name}</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                        {app.job_serial || app.job_id?.slice(0, 8)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[150px] truncate" title={app.job_title}>
                        {app.job_title}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(app.applied_at)}</TableCell>
                    <TableCell>
                      <span className="text-sm">{app.qualification || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{app.experience || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {app.cv_url ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadCV(app.cv_url)}
                          className="text-teal-600 hover:text-teal-700"
                          data-testid={`download-cv-${app.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-slate-400 text-xs">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-xs text-slate-500 font-medium">Change Status</span>
                        <Select 
                          value={app.status || 'new'} 
                          onValueChange={(value) => {
                            setSelectedApp(app);
                            setNewStatus(value);
                            setStatusNotes(app.status_notes || '');
                            setShowStatusModal(true);
                          }}
                        >
                          <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`status-dropdown-${app.id}`}>
                            <SelectValue>
                              {getStatusBadge(app.status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {APPLICATION_STATUSES.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                <span className={`px-2 py-0.5 rounded text-xs ${s.color}`}>{s.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[100px] truncate text-xs text-slate-600" title={app.status_notes}>
                        {app.status_notes || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewProfile(app)}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Profile"
                          data-testid={`view-profile-${app.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <span className="text-sm text-slate-600">
              Page {currentPage} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Application Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Candidate</label>
              <p className="text-slate-900 font-medium">{selectedApp?.candidate_name}</p>
              <p className="text-sm text-slate-500">{selectedApp?.job_title}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Status *</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPLICATION_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Notes</label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                rows={3}
                data-testid="status-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
              <Button onClick={handleStatusUpdate} className="bg-teal-600 hover:bg-teal-700" data-testid="save-status-btn">
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile View Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate Profile</DialogTitle>
          </DialogHeader>
          {viewingProfile && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="flex items-start gap-4">
                {viewingProfile.profile_picture ? (
                  <img 
                    src={getAssetUrl(viewingProfile.profile_picture)} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center">
                    <span className="text-2xl font-bold text-teal-600">
                      {viewingProfile.full_name?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900">{viewingProfile.full_name}</h3>
                  <p className="text-slate-600">{viewingProfile.email}</p>
                  <p className="text-slate-600">{viewingProfile.mobile}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-slate-600">
                      <strong>Gender:</strong> {viewingProfile.gender || '-'}
                    </span>
                    <span className="text-slate-600">
                      <strong>DOB:</strong> {viewingProfile.dob || '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Address</h4>
                <p className="text-slate-600">
                  {[viewingProfile.address, viewingProfile.city, viewingProfile.district, viewingProfile.state, viewingProfile.pincode]
                    .filter(Boolean).join(', ') || 'Not provided'}
                </p>
              </div>

              {/* Education */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Education
                </h4>
                {viewingProfile.education?.length > 0 ? (
                  <div className="space-y-3">
                    {viewingProfile.education.map((edu, i) => (
                      <div key={i} className="bg-slate-50 p-3 rounded-lg">
                        <p className="font-medium text-slate-900">{edu.qualification} - {edu.course}</p>
                        <p className="text-sm text-slate-600">{edu.institution_name}</p>
                        <p className="text-xs text-slate-500">{edu.from_year} - {edu.to_year} | {edu.course_type}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No education details</p>
                )}
              </div>

              {/* Experience */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Experience ({viewingProfile.total_experience || 0} years)
                </h4>
                {viewingProfile.experience?.length > 0 ? (
                  <div className="space-y-3">
                    {viewingProfile.experience.map((exp, i) => (
                      <div key={i} className="bg-slate-50 p-3 rounded-lg">
                        <p className="font-medium text-slate-900">{exp.designation}</p>
                        <p className="text-sm text-slate-600">{exp.company_name}</p>
                        <p className="text-xs text-slate-500">{expFromDate(exp)} - {expToDate(exp)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No experience details (Fresher)</p>
                )}
              </div>

              {/* Skills */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Skills</h4>
                {viewingProfile.skills?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {viewingProfile.skills.map((skill, i) => (
                      <Badge key={i} variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No skills listed</p>
                )}
              </div>

              {/* Languages */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Languages</h4>
                {viewingProfile.languages?.length > 0 ? (
                  <p className="text-slate-600">{viewingProfile.languages.join(', ')}</p>
                ) : (
                  <p className="text-slate-500 text-sm">No languages listed</p>
                )}
              </div>

              {/* CV Download */}
              {viewingProfile.cv && (
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => downloadCV(viewingProfile.cv)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CV
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationManagement;
